import { AbstractControlOptions, NonNullableFormBuilder } from '@angular/forms';
import { Type } from '@angular/core';
import { FilterOperator } from '../../../../api/api.operators';
import {
  booleanFilters,
  dateFilters,
  dateNotFilters,
  enumFilters,
  enumNotFilters,
  numberFilters,
  numberNotFilters,
  stringFilters,
} from '../../../filters/filter.util';
import { BlobLocationFormControl } from '../../../forms/image-upload/blob-location-form-control';

interface FieldConfig {
  label?: string;
  type?: () => any;
  readonly?: boolean;
  default?: () => any;
  formControlFactory?: (fb: NonNullableFormBuilder) => AbstractControlOptions;
  includeFilter?: boolean;
  operators?: FilterOperator[];
  notOperators?: FilterOperator[];
  enumLabels?: Record<any, string>;
}

export interface FieldData {
  key: string | symbol;
  label: string;
  type: () => any;
  operators?: FilterOperator[];
  notOperators?: FilterOperator[];
  readonly?: boolean;
  default?: any;
  component?: Type<any>;
  array?: boolean;
  enumLabels?: Record<any, string>;
  formControlFactory?: (fb: NonNullableFormBuilder) => AbstractControlOptions;
  includeFilter?: boolean;
}

export const FIELDS = Symbol('ResourceField');
export const Field =
  (config: FieldConfig = {}): PropertyDecorator =>
  (target, propertyKey) => {
    validateFieldConfig(config);

    const fields = {
      ...(Reflect.getMetadata(FIELDS, target.constructor) ?? {}),
    };
    const isEnum = !!config.enumLabels;
    const isArray =
      Reflect.getMetadata('design:type', target, propertyKey) === Array;
    fields[propertyKey] = {
      key: propertyKey,
      label: config.label ?? propertyKey,
      type: createFieldTypeResolver(config, target, propertyKey),
      readonly: config.readonly ?? false,
      default: config.default ? config.default() : undefined,
      array: isArray,
      enumLabels: config.enumLabels,
      operators: isEnum && isArray ? [FilterOperator.LIKE] : config.operators,
      notOperators:
        isEnum && isArray ? [FilterOperator.LIKE] : config.notOperators,
      formControlFactory: config.formControlFactory,
      includeFilter: config.includeFilter ?? true,
    } as FieldData;
    Reflect.defineMetadata(FIELDS, fields, target.constructor);
  };

function createFieldTypeResolver(
  config: FieldConfig,
  target: Object,
  propertyKey: string | symbol,
) {
  return () => {
    const type =
      (config.type ?? (() => null))() ??
      Reflect.getMetadata('design:type', target, propertyKey);
    const errorMessage = `Could not establish proper type of '${target.constructor.name}.${String(propertyKey)}' column. Received: ${type}. `;
    if (!type) throw new Error(errorMessage);
    if (type == Array)
      throw new Error(
        errorMessage +
          `If type is an array, please provide the type of the array elements.`,
      );
    return type;
  };
}

export function validateFieldConfig(config: FieldConfig): void {
  if (
    config.default &&
    (typeof config.default !== 'function' ||
      config.default.prototype !== undefined)
  ) {
    throw new Error(
      'Default should be a function which returns the default value',
    );
  }

  if (
    config.type &&
    (typeof config.type !== 'function' || config.type.prototype !== undefined)
  ) {
    throw new Error('Type should be a function which returns the type value');
  }
}

export const StringField = (config: FieldConfig = {}) =>
  Field({
    operators: stringFilters(),
    notOperators: stringFilters(),
    ...config,
  });

export const DateField = (config: FieldConfig = {}) =>
  Field({
    operators: dateFilters(),
    notOperators: dateNotFilters(),
    ...config,
  });

export const NumberField = (config: FieldConfig = {}) =>
  Field({
    operators: numberFilters(),
    notOperators: numberNotFilters(),
    ...config,
  });

export const BooleanField = (config: FieldConfig = {}) =>
  Field({
    operators: booleanFilters(),
    notOperators: booleanFilters(),
    ...config,
  });

export const EnumField = (
  config: FieldConfig & { enumLabels: Record<string, string> },
) =>
  Field({
    operators: enumFilters(),
    notOperators: enumNotFilters(),
    ...config,
  });

export const BlobField = (config: FieldConfig = {}) =>
  Field({
    type: () => Blob,
    includeFilter: false,
    formControlFactory: () => new BlobLocationFormControl(null),
    ...config,
  });

export const CoordinatesField = (config: FieldConfig = {}) =>
  Field({
    includeFilter: false,
    ...config,
  });
